import { useCallback, useEffect, useState } from 'react';
import { api } from '@/lib/api';
import { useAuth } from '@/lib/auth';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Button } from '@/components/ui/button';
import { toast } from 'sonner';
import { AlertCircle, Building2, CheckCircle, Clock, CreditCard, DollarSign, Landmark, Save, Wallet, XCircle, Zap } from 'lucide-react';
import { AdminEmptyState, AdminLoadingState, AdminPageHeader, AdminSurface } from '@/components/admin/AdminPrimitives';

function StatusBadge({ active, activeLabel = 'Enabled', inactiveLabel = 'Disabled' }) {
  return (
    <span className={`inline-flex items-center rounded-full px-2.5 py-1 text-xs font-semibold ${active ? 'bg-emerald-100 text-emerald-700' : 'bg-slate-100 text-slate-600'}`}>
      {active ? activeLabel : inactiveLabel}
    </span>
  );
}

function RuntimeBadge({ ok, label }) {
  return (
    <span className={`inline-flex items-center rounded-full px-2.5 py-1 text-xs font-semibold ${ok ? 'bg-emerald-100 text-emerald-700' : 'bg-amber-100 text-amber-700'}`}>
      {label}: {ok ? 'YES' : 'NO'}
    </span>
  );
}

export default function AdminDonations() {
  const { isSuperAdmin } = useAuth();
  const [settings, setSettings] = useState(null);
  const [transactions, setTransactions] = useState([]);
  const [stats, setStats] = useState({ total_donations: 0, total_amount: 0, pending: 0 });
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const requests = [api.get('/admin/donation-settings')];
      if (isSuperAdmin()) {
        requests.push(api.get('/admin/donations'));
        requests.push(api.get('/admin/donations/stats'));
      }
      const results = await Promise.all(requests);
      setSettings(results[0].data);
      if (isSuperAdmin()) {
        setTransactions(results[1].data);
        setStats(results[2].data);
      }
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to load donation settings');
    } finally {
      setLoading(false);
    }
  }, [isSuperAdmin]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const updateMethod = (method, field, value) => {
    setSettings((prev) => ({
      ...prev,
      methods: {
        ...prev.methods,
        [method]: {
          ...prev.methods[method],
          [field]: value,
        },
      },
    }));
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      const payload = {
        title: settings.title,
        description: settings.description,
        methods: settings.methods,
      };
      const response = await api.put('/admin/donation-settings', payload);
      setSettings(response.data);
      toast.success('Donation methods updated');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to save donation settings');
    } finally {
      setSaving(false);
    }
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return '';
    return new Date(dateStr).toLocaleDateString('en-US', {
      year: 'numeric', month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit'
    });
  };

  const statusIcon = (status) => {
    switch (status) {
      case 'paid': return <CheckCircle size={16} className="text-green-600" />;
      case 'pending': return <Clock size={16} className="text-amber-500" />;
      case 'initiated': return <AlertCircle size={16} className="text-blue-500" />;
      default: return <XCircle size={16} className="text-red-500" />;
    }
  };

  const statusBadge = (status) => {
    switch (status) {
      case 'paid': return 'bg-green-100 text-green-700';
      case 'pending': return 'bg-amber-100 text-amber-700';
      case 'initiated': return 'bg-blue-100 text-blue-700';
      default: return 'bg-red-100 text-red-700';
    }
  };

  if (loading || !settings) {
    return <AdminLoadingState label="Loading donation settings" />;
  }

  const canManageSensitive = settings.capabilities?.can_manage_sensitive_methods === true;
  const canEditOverview = settings.capabilities?.can_edit_overview === true;

  return (
    <div data-testid="admin-donations-page">
      <AdminPageHeader
        eyebrow="Giving"
        title="Donations"
        description="Manage public donation methods from one shared system."
      >
        <Button data-testid="save-donation-settings" onClick={handleSave} disabled={saving}
          className="px-6">
          <Save size={16} className="mr-2" /> {saving ? 'Saving...' : 'Save Changes'}
        </Button>
      </AdminPageHeader>

      <AdminSurface className="p-6 mb-6 space-y-5">
        <h2 className="text-lg font-bold text-[#0F172A]">Donation Page Content</h2>
        <div>
          <label className="block text-sm font-medium text-[#0F172A] mb-1">Title</label>
          <Input value={settings.title} onChange={(e) => setSettings({ ...settings, title: e.target.value })} className="rounded-xl" disabled={!canEditOverview} />
        </div>
        <div>
          <label className="block text-sm font-medium text-[#0F172A] mb-1">Description</label>
          <Textarea value={settings.description} onChange={(e) => setSettings({ ...settings, description: e.target.value })} className="rounded-xl" rows={3} disabled={!canEditOverview} />
        </div>
      </AdminSurface>

      <div className="grid grid-cols-1 xl:grid-cols-2 gap-6 mb-8">
        <AdminSurface className="p-6 space-y-4">
          <div className="flex items-center justify-between gap-3">
            <div className="flex items-center gap-3">
              <div className="w-12 h-12 bg-[#EBF2FA] rounded-xl flex items-center justify-center">
                <CreditCard size={22} className="text-[#1E4FA1]" />
              </div>
              <div>
                <h2 className="text-lg font-bold text-[#0F172A]">Stripe</h2>
                <p className="text-sm text-[#94A3B8]">Server-side checkout with env-only secrets</p>
              </div>
            </div>
            <StatusBadge active={settings.methods.stripe.enabled} />
          </div>
          <div className="flex flex-wrap gap-2">
            <RuntimeBadge ok={settings.runtime_status?.stripe?.configured} label="Configured" />
            <RuntimeBadge ok={settings.runtime_status?.stripe?.webhook_configured} label="Webhook" />
            <span className="inline-flex items-center rounded-full px-2.5 py-1 text-xs font-semibold bg-[#EAF1F7] text-[#1E4FA1] uppercase">
              Mode: {settings.runtime_status?.stripe?.mode || 'unknown'}
            </span>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-[#0F172A] mb-1">Button Label</label>
              <Input value={settings.methods.stripe.button_label} onChange={(e) => updateMethod('stripe', 'button_label', e.target.value)} className="rounded-xl" disabled={!canManageSensitive} />
            </div>
            <div className="flex items-end">
              <label className="inline-flex items-center gap-2 text-sm font-medium text-[#0F172A]">
                <input type="checkbox" checked={settings.methods.stripe.enabled} onChange={(e) => updateMethod('stripe', 'enabled', e.target.checked)} disabled={!canManageSensitive} />
                Enable Stripe donations
              </label>
            </div>
            <div>
              <label className="block text-sm font-medium text-[#0F172A] mb-1">Preset Amounts</label>
              <Input value={(settings.methods.stripe.preset_amounts || []).join(', ')} onChange={(e) => updateMethod('stripe', 'preset_amounts', e.target.value.split(',').map(v => parseFloat(v.trim())).filter(v => !Number.isNaN(v) && v > 0))} className="rounded-xl" disabled={!canManageSensitive} />
            </div>
            <div>
              <label className="block text-sm font-medium text-[#0F172A] mb-1">Currency</label>
              <Input value={settings.methods.stripe.currency} onChange={(e) => updateMethod('stripe', 'currency', e.target.value.toLowerCase())} className="rounded-xl" disabled={!canManageSensitive} />
            </div>
            <div>
              <label className="block text-sm font-medium text-[#0F172A] mb-1">Minimum Amount</label>
              <Input type="number" value={settings.methods.stripe.min_amount} onChange={(e) => updateMethod('stripe', 'min_amount', parseFloat(e.target.value || '0'))} className="rounded-xl" disabled={!canManageSensitive} />
            </div>
            <div>
              <label className="block text-sm font-medium text-[#0F172A] mb-1">Maximum Amount</label>
              <Input type="number" value={settings.methods.stripe.max_amount} onChange={(e) => updateMethod('stripe', 'max_amount', parseFloat(e.target.value || '0'))} className="rounded-xl" disabled={!canManageSensitive} />
            </div>
          </div>
          {!canManageSensitive ? <p className="text-xs text-[#94A3B8]">Editors can view Stripe method status but cannot enable or configure it.</p> : null}
        </AdminSurface>

        <AdminSurface className="p-6 space-y-4">
          <div className="flex items-center justify-between gap-3">
            <div className="flex items-center gap-3">
              <div className="w-12 h-12 bg-[#EBF2FA] rounded-xl flex items-center justify-center">
                <Wallet size={22} className="text-[#1E4FA1]" />
              </div>
              <div>
                <h2 className="text-lg font-bold text-[#0F172A]">PayPal</h2>
                <p className="text-sm text-[#94A3B8]">Public donation link from secure backend env</p>
              </div>
            </div>
            <StatusBadge active={settings.methods.paypal.enabled} />
          </div>
          <div className="flex flex-wrap gap-2">
            <RuntimeBadge ok={settings.runtime_status?.paypal?.configured} label="Configured" />
            <span className="inline-flex items-center rounded-full px-2.5 py-1 text-xs font-semibold bg-[#EAF1F7] text-[#1E4FA1] uppercase">
              Mode: {settings.runtime_status?.paypal?.mode || 'unknown'}
            </span>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-[#0F172A] mb-1">Button Label</label>
              <Input value={settings.methods.paypal.button_label} onChange={(e) => updateMethod('paypal', 'button_label', e.target.value)} className="rounded-xl" disabled={!canManageSensitive} />
            </div>
            <div className="flex items-end">
              <label className="inline-flex items-center gap-2 text-sm font-medium text-[#0F172A]">
                <input type="checkbox" checked={settings.methods.paypal.enabled} onChange={(e) => updateMethod('paypal', 'enabled', e.target.checked)} disabled={!canManageSensitive} />
                Enable PayPal donations
              </label>
            </div>
          </div>
          <p className="text-xs text-[#94A3B8]">PayPal secrets or hosted donation URLs are not editable here. They stay in backend environment variables only.</p>
        </AdminSurface>

        <AdminSurface className="p-6 space-y-4">
          <div className="flex items-center justify-between gap-3">
            <div className="flex items-center gap-3">
              <div className="w-12 h-12 bg-[#EBF2FA] rounded-xl flex items-center justify-center">
                <Landmark size={22} className="text-[#1E4FA1]" />
              </div>
              <div>
                <h2 className="text-lg font-bold text-[#0F172A]">Bank Transfer</h2>
                <p className="text-sm text-[#94A3B8]">Public manual transfer details</p>
              </div>
            </div>
            <StatusBadge active={settings.methods.bank_transfer.enabled} />
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="md:col-span-2 flex items-end">
              <label className="inline-flex items-center gap-2 text-sm font-medium text-[#0F172A]">
                <input type="checkbox" checked={settings.methods.bank_transfer.enabled} onChange={(e) => updateMethod('bank_transfer', 'enabled', e.target.checked)} disabled={!canManageSensitive} />
                Enable Bank Transfer
              </label>
            </div>
            <div>
              <label className="block text-sm font-medium text-[#0F172A] mb-1">Account Holder</label>
              <Input value={settings.methods.bank_transfer.account_holder} onChange={(e) => updateMethod('bank_transfer', 'account_holder', e.target.value)} className="rounded-xl" />
            </div>
            <div>
              <label className="block text-sm font-medium text-[#0F172A] mb-1">Bank Name</label>
              <Input value={settings.methods.bank_transfer.bank_name} onChange={(e) => updateMethod('bank_transfer', 'bank_name', e.target.value)} className="rounded-xl" />
            </div>
            <div>
              <label className="block text-sm font-medium text-[#0F172A] mb-1">Account Number / IBAN</label>
              <Input value={settings.methods.bank_transfer.account_number} onChange={(e) => updateMethod('bank_transfer', 'account_number', e.target.value)} className="rounded-xl" />
            </div>
            <div>
              <label className="block text-sm font-medium text-[#0F172A] mb-1">IBAN</label>
              <Input value={settings.methods.bank_transfer.iban} onChange={(e) => updateMethod('bank_transfer', 'iban', e.target.value)} className="rounded-xl" />
            </div>
            <div>
              <label className="block text-sm font-medium text-[#0F172A] mb-1">SWIFT / BIC</label>
              <Input value={settings.methods.bank_transfer.swift_bic} onChange={(e) => updateMethod('bank_transfer', 'swift_bic', e.target.value)} className="rounded-xl" />
            </div>
            <div>
              <label className="block text-sm font-medium text-[#0F172A] mb-1">Routing Number</label>
              <Input value={settings.methods.bank_transfer.routing_number} onChange={(e) => updateMethod('bank_transfer', 'routing_number', e.target.value)} className="rounded-xl" />
            </div>
            <div className="md:col-span-2">
              <label className="block text-sm font-medium text-[#0F172A] mb-1">Instructions</label>
              <Textarea value={settings.methods.bank_transfer.instructions} onChange={(e) => updateMethod('bank_transfer', 'instructions', e.target.value)} className="rounded-xl" rows={3} />
            </div>
          </div>
        </AdminSurface>

        <AdminSurface className="p-6 space-y-4">
          <div className="flex items-center justify-between gap-3">
            <div className="flex items-center gap-3">
              <div className="w-12 h-12 bg-[#EBF2FA] rounded-xl flex items-center justify-center">
                <Zap size={22} className="text-[#1E4FA1]" />
              </div>
              <div>
                <h2 className="text-lg font-bold text-[#0F172A]">Zelle</h2>
                <p className="text-sm text-[#94A3B8]">Public direct transfer recipient details</p>
              </div>
            </div>
            <StatusBadge active={settings.methods.zelle.enabled} />
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="md:col-span-2 flex items-end">
              <label className="inline-flex items-center gap-2 text-sm font-medium text-[#0F172A]">
                <input type="checkbox" checked={settings.methods.zelle.enabled} onChange={(e) => updateMethod('zelle', 'enabled', e.target.checked)} disabled={!canManageSensitive} />
                Enable Zelle
              </label>
            </div>
            <div>
              <label className="block text-sm font-medium text-[#0F172A] mb-1">Recipient Name</label>
              <Input value={settings.methods.zelle.recipient_name} onChange={(e) => updateMethod('zelle', 'recipient_name', e.target.value)} className="rounded-xl" />
            </div>
            <div>
              <label className="block text-sm font-medium text-[#0F172A] mb-1">Recipient Email</label>
              <Input value={settings.methods.zelle.recipient_email} onChange={(e) => updateMethod('zelle', 'recipient_email', e.target.value)} className="rounded-xl" />
            </div>
            <div className="md:col-span-2">
              <label className="block text-sm font-medium text-[#0F172A] mb-1">Recipient Phone</label>
              <Input value={settings.methods.zelle.recipient_phone} onChange={(e) => updateMethod('zelle', 'recipient_phone', e.target.value)} className="rounded-xl" />
            </div>
            <div className="md:col-span-2">
              <label className="block text-sm font-medium text-[#0F172A] mb-1">Instructions</label>
              <Textarea value={settings.methods.zelle.instructions} onChange={(e) => updateMethod('zelle', 'instructions', e.target.value)} className="rounded-xl" rows={3} />
            </div>
          </div>
        </AdminSurface>
      </div>

      {isSuperAdmin() ? (
        <>
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mb-8">
            <AdminSurface className="p-6">
              <div className="w-11 h-11 bg-green-50 text-green-600 rounded-xl flex items-center justify-center mb-3">
                <DollarSign size={20} />
              </div>
              <p className="mscm-admin-stat-value text-2xl font-bold">${stats.total_amount.toFixed(2)}</p>
              <p className="text-sm text-[#94A3B8] mt-0.5">Total Received</p>
            </AdminSurface>
            <AdminSurface className="p-6">
              <div className="w-11 h-11 bg-blue-50 text-[#1E4FA1] rounded-xl flex items-center justify-center mb-3">
                <Building2 size={20} />
              </div>
              <p className="mscm-admin-stat-value text-2xl font-bold">{stats.total_donations}</p>
              <p className="text-sm text-[#94A3B8] mt-0.5">Successful Donations</p>
            </AdminSurface>
            <AdminSurface className="p-6">
              <div className="w-11 h-11 bg-amber-50 text-amber-600 rounded-xl flex items-center justify-center mb-3">
                <Clock size={20} />
              </div>
              <p className="mscm-admin-stat-value text-2xl font-bold">{stats.pending}</p>
              <p className="text-sm text-[#94A3B8] mt-0.5">Pending</p>
            </AdminSurface>
          </div>

          {transactions.length === 0 ? (
            <AdminEmptyState icon={DollarSign} title="No donation transactions yet" description="Completed or pending donation records will appear here." />
          ) : (
            <AdminSurface className="overflow-hidden">
              <div className="overflow-x-auto">
                <table className="mscm-admin-table w-full" data-testid="donations-table">
                  <thead>
                    <tr className="border-b border-[#E2E8F0] bg-[#F8FAFC]">
                      <th className="text-left px-6 py-3 text-xs font-semibold text-[#475569] uppercase tracking-wider">Date</th>
                      <th className="text-left px-6 py-3 text-xs font-semibold text-[#475569] uppercase tracking-wider">Method</th>
                      <th className="text-left px-6 py-3 text-xs font-semibold text-[#475569] uppercase tracking-wider">Amount</th>
                      <th className="text-left px-6 py-3 text-xs font-semibold text-[#475569] uppercase tracking-wider">Status</th>
                      <th className="text-left px-6 py-3 text-xs font-semibold text-[#475569] uppercase tracking-wider">Reference</th>
                    </tr>
                  </thead>
                  <tbody>
                    {transactions.map((tx) => (
                      <tr key={tx.id} className="border-b border-[#E2E8F0] last:border-0 hover:bg-[#F8FAFC]">
                        <td className="px-6 py-4 text-sm text-[#475569]">{formatDate(tx.created_at)}</td>
                        <td className="px-6 py-4 text-sm font-medium text-[#0F172A] capitalize">{tx.method || 'stripe'}</td>
                        <td className="px-6 py-4 text-sm font-semibold text-[#0F172A]">
                          ${(tx.amount || 0).toFixed(2)} <span className="text-[#94A3B8] font-normal uppercase">{tx.currency || 'usd'}</span>
                        </td>
                        <td className="px-6 py-4">
                          <span className={`inline-flex items-center gap-1.5 text-xs px-2.5 py-1 rounded-full font-medium ${statusBadge(tx.payment_status)}`}>
                            {statusIcon(tx.payment_status)}
                            {tx.payment_status || 'unknown'}
                          </span>
                        </td>
                        <td className="px-6 py-4 text-xs text-[#94A3B8] font-mono truncate max-w-[200px]">{tx.session_id || tx.id}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </AdminSurface>
          )}
        </>
      ) : null}
    </div>
  );
}
